import React, { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { AdvancedMarker, AdvancedMarkerAnchorPoint, ControlPosition, MapControl, useMap } from '@vis.gl/react-google-maps';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import DirectionsBoat from '@mui/icons-material/DirectionsBoat';
import Train from '@mui/icons-material/Train';
import Close from '@mui/icons-material/Close';
import type { DriveTime } from '../../../types/Supplier';
import type { FreightPlace } from '../../../types/FreightPlace';
import { getFreightPlaces } from '../requests/freight-place-requests';

// Map conventions (USGS): water is blue, railroads are black. The port blue is far darker than the search
// map's truck-stop blue and the yard circle, and neither clashes with orange request-space or green pins.
const PLACE_COLORS: Record<FreightPlace['type'], string> = { PORT: '#0D47A1', RAIL_RAMP: '#212121' };

const pulse = keyframes`
  from { transform: scale(1); opacity: 0.6; }
  to { transform: scale(2.6); opacity: 0; }
`;

/** How long a picked place and the yard stay highlighted: 5 × 1.2 s, about 6 s. */
export const HIGHLIGHT_TIMING = '1.2s ease-out 5';
const PLACE_ICONS = { PORT: DirectionsBoat, RAIL_RAMP: Train };
// The legend's words, short enough to keep the place panel's first line to one line with a railroad.
const PLACE_LABELS: Record<FreightPlace['type'], string> = { PORT: 'Port', RAIL_RAMP: 'Rail ramp' };
const RAILROAD_NAMES: Record<string, string> = {
  BNSF: 'BNSF Railway', UP: 'Union Pacific', CSX: 'CSX', NS: 'Norfolk Southern', CN: 'Canadian National',
  CPKC: 'CPKC', FEC: 'Florida East Coast', IAIS: 'Iowa Interstate',
};

// Below this the map is more than about 100 mi across and the icons pile up.
const MIN_ZOOM = 8;

// Between a clicked icon and the place panel, once the map has moved it out from under the panel.
const PANEL_GAP = 8;

/** Rings growing out of the parent (which needs position: relative); skipped for reduced motion. */
export const PulseRing = ({ color, onEnd }: { color: string, onEnd?: () => void }) =>
  <Box component="span" data-pulse aria-hidden="true" onAnimationEnd={onEnd} sx={{
    position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: color,
    animation: `${pulse} ${HIGHLIGHT_TIMING}`,
    '@media (prefers-reduced-motion: reduce)': { display: 'none' },
  }} />;

interface FreightPlaceIconProps {
  type: FreightPlace['type'];
  size: number;
  /** The place's name, on a map icon, so the map can find it to keep it clear of the place panel. */
  name?: string;
  /** Rings around the badge to point out the place; a new number restarts them. */
  pulseRun?: number;
  onPulseEnd?: () => void;
}

/** A round ship or train badge, also used by the map legend. */
export const FreightPlaceIcon = ({ type, size, name, pulseRun, onPulseEnd }: FreightPlaceIconProps) => {
  const Icon = PLACE_ICONS[type];
  return <div data-place-icon={name} style={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
    {pulseRun !== undefined && <PulseRing key={pulseRun} color={PLACE_COLORS[type]} onEnd={onPulseEnd} />}
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      borderRadius: '50%',
      border: '2px solid #FFFFFF',
      background: PLACE_COLORS[type],
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Icon style={{ fontSize: Math.round(size * 0.6) }} aria-hidden="true" />
    </div>
  </div>;
};

interface PlacePanelProps {
  place: FreightPlace;
  driveTime?: DriveTime;
  onClose: () => void;
  /** Measured to keep the clicked icon from ending up under the panel. */
  panelRef: RefObject<HTMLDivElement | null>;
}

/**
 * A clicked port or rail ramp, in the map's bottom-left corner above Google's logo, where it covers neither the
 * icon nor the yard. As small as its text allows, so it hides little of the map.
 */
const PlacePanel = ({ place, driveTime, onClose, panelRef }: PlacePanelProps) => <MapControl position={ControlPosition.LEFT_BOTTOM}>
  <Box ref={panelRef} data-place-panel role="region" aria-label={place.name} sx={{
    display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: 240, boxSizing: 'border-box', m: '0 0 8px 10px',
    p: '8px 6px 8px 10px', bgcolor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  }}>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#6B7280', fontSize: 11, fontWeight: 600 }}>
        <Box component="span" sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: '50%', bgcolor: PLACE_COLORS[place.type] }} />
        {PLACE_LABELS[place.type]}{place.railroad && ` · ${RAILROAD_NAMES[place.railroad] ?? place.railroad}`}
      </Box>
      <Box component="h3" sx={{ m: '1px 0 0', color: '#1F2937', fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>
        {place.name}
      </Box>
      {driveTime && <Box sx={{ mt: '2px', color: '#1F2937', fontSize: 13, fontWeight: 500 }}>
        {`${driveTime.miles} mi · ${driveTime.minutes} min drive`}
      </Box>}
    </Box>
    <Box component="button" type="button" aria-label={`Close ${place.name}`} title="Close" onClick={onClose} sx={{
      flexShrink: 0, width: 24, height: 24, m: '-2px 0 0', p: 0, border: 0, borderRadius: '50%', bgcolor: 'transparent',
      color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      '&:hover': { bgcolor: '#F3F4F6', color: '#1F2937' },
    }}>
      <Close sx={{ fontSize: 18 }} aria-hidden="true" />
    </Box>
  </Box>
</MapControl>;

/** The map's current zoom, kept in step with zoom_changed. */
export const useMapZoom = () => {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map?.getZoom() ?? 0);
  useEffect(() => {
    if (!map) {
      return;
    }
    const listener = map.addListener('zoom_changed', () => setZoom(map.getZoom() ?? 0));
    return () => listener.remove();
  }, [map]);
  return zoom;
};

export interface NearbyFocus {
  name: string;
  lat: number;
  lng: number;
}

const FOCUS_PADDING = { right: 60, bottom: 40, left: 60 };
// The yard pin stands 38 px above its point; 8 px more keeps its head off the chip row.
const PIN_CLEARANCE = 46;

interface NearbyFocusControllerProps {
  area?: google.maps.LatLngLiteral;
  focus?: NearbyFocus;
  /** Scrolled to the top of the window; give it a scroll-margin-top that clears the sticky site header. */
  scrollTarget?: RefObject<HTMLElement | null>;
  /** The chip row over the map's top, one row on a desktop and two on a phone; the fit keeps the pin below it. */
  overlay?: RefObject<HTMLElement | null>;
}

/**
 * Zooms the map just enough to show the yard and a place picked from the Nearby list, and scrolls the map
 * into view. Mounted without the icons too, since only they need a map ID.
 */
export const NearbyFocusController = ({ area, focus, scrollTarget, overlay }: NearbyFocusControllerProps) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !focus) {
      return;
    }
    // maxZoom caps a very close place.
    const yard = area ?? focus;
    const overlayBottom = overlay?.current ? overlay.current.offsetTop + overlay.current.offsetHeight : 0;
    map.fitBounds({
      north: Math.max(yard.lat, focus.lat), south: Math.min(yard.lat, focus.lat),
      east: Math.max(yard.lng, focus.lng), west: Math.min(yard.lng, focus.lng),
    }, { ...FOCUS_PADDING, top: overlayBottom + PIN_CLEARANCE });
    // 'start' plus the target's scroll margin keeps the whole map below the sticky header; 'center' put its
    // top under the header in a short window.
    scrollTarget?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [map, focus, area, scrollTarget, overlay]);

  return null;
};

interface FreightPlaceMarkersProps {
  driveTimes?: DriveTime[];
  /** A place picked from the Nearby list; each new object pulses its icon from the start. */
  focus?: NearbyFocus;
}

/**
 * Ports and rail ramps as icons. The yard's Nearby places (from its drive times) are drawn larger and stay
 * on the map however far out it zooms; the rest appear from MIN_ZOOM in.
 */
const FreightPlaceMarkers = ({ driveTimes, focus }: FreightPlaceMarkersProps) => {
  const map = useMap();
  const [places, setPlaces] = useState<FreightPlace[]>([]);
  const zoom = useMapZoom();
  const [selected, setSelected] = useState<FreightPlace>();
  const panel = useRef<HTMLDivElement>(null);
  const [pulse, setPulse] = useState<{ name: string, run: number }>();
  const nearby = useMemo(() => new globalThis.Map((driveTimes ?? []).map((driveTime) => [driveTime.name, driveTime])), [driveTimes]);

  useEffect(() => {
    let stale = false;
    // A failed fetch only means no icons; the profile itself still works.
    getFreightPlaces().then((list) => !stale && setPlaces(list)).catch(() => undefined);
    return () => { stale = true; };
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }
    const clickListener = map.addListener('click', () => setSelected(undefined));
    return () => clickListener.remove();
  }, [map]);

  useEffect(() => {
    if (!focus) {
      return;
    }
    // A panel left open from an earlier click would describe a different place from the one now in view.
    setSelected(undefined);
    // Only ports and rail ramps have an icon to pulse; a downtown just gets the zoom.
    setPulse((previous) => ({ name: focus.name, run: (previous?.run ?? 0) + 1 }));
  }, [focus]);

  useEffect(() => {
    if (!map || !selected) {
      return;
    }
    // The panel sits in the bottom-left corner, so an icon there ends up under it: move the map up or right,
    // whichever is the shorter way out. Google places the panel's map control a moment after it renders, so look
    // again until the panel has a box: up to half a second.
    let timer = 0;
    let triesLeft = 10;
    const keepClear = () => {
      const covered = panel.current?.getBoundingClientRect();
      if (!covered?.width) {
        timer = triesLeft-- > 0 ? window.setTimeout(keepClear, 50) : 0;
        return;
      }
      const icon = Array.from(map.getDiv().querySelectorAll<HTMLElement>('[data-place-icon]'))
        .find((element) => element.dataset.placeIcon === selected.name)?.getBoundingClientRect();
      const clear = !icon || icon.left >= covered.right + PANEL_GAP || icon.right <= covered.left - PANEL_GAP
        || icon.bottom <= covered.top - PANEL_GAP || icon.top >= covered.bottom + PANEL_GAP;
      if (clear) {
        return;
      }
      const up = icon.bottom - covered.top + PANEL_GAP;
      const right = covered.right + PANEL_GAP - icon.left;
      map.panBy(right < up ? -right : 0, right < up ? 0 : up);
    };
    timer = window.setTimeout(keepClear);
    return () => window.clearTimeout(timer);
  }, [map, selected]);

  return <>
    {places.map((place) => {
      const isNearby = nearby.has(place.name);
      if (!isNearby && zoom < MIN_ZOOM) {
        return null;
      }
      return <AdvancedMarker key={`${place.type}:${place.name}`}
        position={{ lat: place.lat, lng: place.lng }}
        title={place.name}
        anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
        zIndex={isNearby ? 2 : 1}
        onClick={() => setSelected(place)}>
        <FreightPlaceIcon type={place.type} size={isNearby ? 32 : 24} name={place.name}
          pulseRun={pulse?.name === place.name ? pulse.run : undefined} onPulseEnd={() => setPulse(undefined)} />
      </AdvancedMarker>;
    })}
    {selected && <PlacePanel place={selected} driveTime={nearby.get(selected.name)} onClose={() => setSelected(undefined)}
      panelRef={panel} />}
  </>;
};

export default FreightPlaceMarkers;
